import React, { createContext, useContext, useState, useEffect } from "react";
import { BehaviorSubject } from "rxjs";

const LanguageContext = createContext();

// Observable para transmitir cambios de idioma
export const currentLanguage$ = new BehaviorSubject(getInitialLanguage());

// Función para verificar disponibilidad de localStorage
export const isLocalStorageEnabled = () => {
  try {
    const key = `__storage__test`;
    window.localStorage.setItem(key, null);
    window.localStorage.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Determina el idioma inicial basado en la URL y localStorage
 * @returns {string} El código de idioma a utilizar
 */
export function getInitialLanguage() {
  try {
    // Obtener el código del país de la URL (primer segmento del path)
    const countryCode = window.location.pathname.split("/")[1];

    // Verificar si hay un idioma guardado en localStorage
    const storedLang = localStorage.getItem("lang");

    // Determinar el idioma según las reglas especificadas
    if (storedLang) {
      // Si hay un idioma guardado en localStorage, usamos esa configuración
      if (storedLang === "pt") {
        return "pt-BR";
      } else if (countryCode === "ar") {
        return "es-AR";
      } else {
        return "es";
      }
    } else {
      // Si no hay un idioma guardado, usamos la URL
      if (countryCode === "br" || countryCode === "pt") {
        // Para Brasil o Portugal, usar portugués de Brasil
        localStorage.setItem("lang", "pt");
        return "pt-BR";
      } else if (countryCode === "ar") {
        // Para Argentina, usar español de Argentina
        localStorage.setItem("lang", "es");
        return "es-AR";
      } else {
        // Para otros países, usar español genérico
        localStorage.setItem("lang", "es");
        return "es";
      }
    }
  } catch (error) {
    console.error("Error al detectar el idioma:", error);
    return "es"; // Valor predeterminado en caso de error
  }
}

// Función para obtener el idioma de la URL
export const getLanguageFromUrl = () => {
  const url = location.pathname;
  let languageCode;

  if (isLocalStorageEnabled()) {
    try {
      // Prioridad: localStorage si existe
      if (localStorage.getItem("lang")) {
        languageCode =
          localStorage.getItem("lang") === "pt"
            ? "br"
            : url.split("/")[1] === "ar"
            ? "ar"
            : "es";
      } else {
        // Si no hay localStorage, determinar por URL
        const geo = url.split("/")[1];
        languageCode = geo === "pt" ? "br" : geo;
      }
    } catch (error) {
      console.error(error);
    }
  } else {
    const geo = url.split("/")[1];
    languageCode = geo === "pt" ? "br" : geo;
  }

  // Guardar el idioma en localStorage para futuras visitas
  if (isLocalStorageEnabled()) {
    localStorage.setItem("lang", languageCode === "br" ? "pt" : "es");
  }

  // Mapear códigos de país a códigos de idioma
  switch (languageCode) {
    case "br":
    case "pt":
      return "pt-BR";
    case "ar":
      return "es-AR";
    default:
      return "es";
  }
};

/**
 * Cambia el idioma actual
 * @param {string} newLanguage - El nuevo idioma a establecer
 */
export function changeLanguage(newLanguage) {
  try {
    // Guardar forma corta del idioma en localStorage
    const shortLang = newLanguage.startsWith("pt") ? "pt" : "es";
    localStorage.setItem("lang", shortLang);

    // Actualizar la geolocalización basada en el idioma seleccionado
    const { getGeo } = require("@a365/api");
    if (shortLang === "pt") {
      // Para portugués, siempre usar BR como country_code
      getGeo(true, true, "BR");
    } else {
      // Para español, usar el código de país actual
      const currentCountryCode = window.location.pathname.split("/")[1];
      getGeo(true, true, currentCountryCode);
    }

    // Notificar a todos los suscriptores DESPUÉS de actualizar geo
    currentLanguage$.next(newLanguage);

    console.info(`Idioma cambiado a: ${newLanguage}`);
  } catch (error) {
    console.error("Error al cambiar el idioma:", error);
  }
}

// Hook personalizado para usar el idioma
export const useLanguage = () => {
  return useContext(LanguageContext);
};

// Proveedor de contexto de idioma
export const LanguageProvider = ({ children, i18nInstance }) => {
  const [language, setLanguage] = useState(getLanguageFromUrl);

  const changeLanguageHandler = (newLanguage) => {
    if (i18nInstance) {
      i18nInstance.changeLanguage(newLanguage);
    }
    setLanguage(newLanguage);

    // Actualizar localStorage con el nuevo idioma
    if (isLocalStorageEnabled()) {
      localStorage.setItem("lang", newLanguage.includes("pt") ? "pt" : "es");
    }
  };

  useEffect(() => {
    // Suscribirse a cambios en el observable currentLanguage$
    const subscription = currentLanguage$.subscribe((newLang) => {
      changeLanguageHandler(newLang);
    });

    return () => {
      // Limpiar suscripción al desmontar
      subscription.unsubscribe();
    };
  }, [i18nInstance]);

  useEffect(() => {
    if (i18nInstance) {
      i18nInstance.changeLanguage(language);
    }
  }, [language, i18nInstance]);

  const contextValue = {
    language,
    setLanguage: (newLanguage) => {
      changeLanguage(newLanguage); // Esto actualizará currentLanguage$
    },
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Inicializa el servicio de idioma
 */
export function initLanguageService() {
  // Establecer el idioma inicial
  const initialLang = getInitialLanguage();
  currentLanguage$.next(initialLang);

  // Escuchar cambios en la URL
  window.addEventListener("popstate", () => {
    const newLang = getInitialLanguage();
    currentLanguage$.next(newLang);
  });

  console.info(`Servicio de idioma inicializado con: ${initialLang}`);
}

/**
 * Comprueba si hay un parámetro de idioma en la URL y lo aplica
 */
export function handleLanguageParam() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("lang")) {
      const lang = urlParams.get("lang");
      const fullLang = lang === "pt" ? "pt-BR" : "es";
      changeLanguage(fullLang);
    }
  } catch (error) {
    console.error("Error procesando parámetro de idioma:", error);
  }
}

// Función auxiliar para inicializar el idioma
export const initializeLanguage = (i18nInstance) => {
  const language = getLanguageFromUrl();
  if (i18nInstance) {
    i18nInstance.changeLanguage(language);
  }
  return language;
};
